/**
 * Per-game Penelope control hook — the data half of the control on
 * /live/:gameKey. Penelope is the per-trader Pinnacle maker bot
 * (relay/src/services/penelope). A game is shadow by default (decides and
 * simulates, never writes to Kalshi); its baseline goes live only when the box
 * allows it and the trader holds the live grant.
 *
 * One read: GET /api/penelope/games/by-event/:eventTicker returns the caller's
 * own game for this event (or null) and whether one can be attached. Every
 * mutation is a relay desired-state write the conductor enacts within a second;
 * the relay re-checks the grant on each one.
 */
import { useCallback, useMemo, useState } from 'react';
import { apiFetch } from '@/stores/authStore';
import { eventTickerOfMarket } from '@/lib/vanilla/slate';
import type { GameRow } from '@/views/ValueDashboard/types';
import { larryLegsOf } from '@/views/Cockpit/larryMatch';
import { useVisibilityAwarePoll } from './useVisibilityAwarePoll';

const POLL_MS = 2_000;

export type PenelopeLean = 'neutral' | 'a' | 'b';
/** One resting order of hers. `orderId` / `why` are absent from relays before PEN-01 (2026-10-10). */
export interface PenelopeRest {
  px: number; size: number; orderId?: string; why?: string; /** what it went in at (size = what is left) */ placed?: number;
  /** Relay ≥ R10 ("Why?"): when it went in (wall ms), how old it is, what the placing decision saw, and its slot. */
  placedAtMs?: number | null; ageMs?: number; edgeC?: number | null; fairC?: number | null; regime?: PenelopeRegime | null; slot?: 'main' | 'aux';
}
/** Relay ≥ R10 ("Why?"): one side's touch as she judged it, whether buying it adds, and an unknown place's hold. */
export interface PenelopeSideNote { raw: number | null; touch: number | null; thin: boolean; entry: boolean; unknownHoldMs: number }
export type PenelopeRegime = 'IDLE' | 'VALUE' | 'PULL' | 'PAIR' | 'HOLD' | 'PAUSED' | 'STOPPED' | 'HARVEST' | 'MINI';
export type PenelopeLiquidityGatePolicy = 'pause' | 'hold' | 'quote';
/** A researcher's own variant: the game's config with `overlay` applied, run beside the baseline in simulation. */
export interface PenelopeVariantSpec { name: string; overlay: Record<string, unknown>; legs?: 'noNo' | 'yesYes' | 'oneMarket' }
/** A built-in variant by name, or a custom spec. */
export type PenelopeVariant = string | PenelopeVariantSpec;
export const isVariantSpec = (v: PenelopeVariant): v is PenelopeVariantSpec => typeof v === 'object' && v !== null;
export const variantName = (v: PenelopeVariant): string => (isVariantSpec(v) ? v.name : v);

/** The knobs the control edits. The relay holds more (see PenelopeGameConfig); unknown keys ride along untouched. */
export interface PenelopeConfig {
  enabled: boolean;
  mode: 'shadow' | 'live';
  lean: PenelopeLean;
  clipContracts: number;
  /** C02-F02: the clip of a Pinnacle-qualified sell-back; 0 / absent = Contract Size (relay ≥ 2026-10-15). */
  sellBackClipContracts?: number;
  /** Doom 10-16: a short-side entry sized by Contract Size (default) or by the Sell-back Contract Size. */
  shortEntryClip?: 'contract' | 'sellBack';
  /** Doom 10-17: the larger clip at a larger net edge (entries only; never a size-only move of a resting order). */
  edgeSizeEnabled?: boolean;
  edgeSizeThresholdCents?: number;
  edgeSizeClipContracts?: number;
  maxNetContracts: number;
  leanMaxNetContracts: number;
  maxGrossContracts: number;
  /** PEN-05 entry band (relay default 3 / 97, inclusive): an adding order only at a price within it. */
  bidFloorCents?: number;
  bidCeilingCents?: number;
  minEdgeCents: number;
  cancelEdgeCents: number;
  pairEnabled: boolean;
  pairLockCents: number;
  pullPolicy: 'cancelAll' | 'cancelAdding' | 'hold' | 'widen';
  /** What may take a resting order off (relay default stay); pullPolicy applies under chase only. */
  restPolicy?: 'stay' | 'chase';
  /** Volatility harvester rows: depth (contracts, top 4 levels) a filled side waits for, and for how long. */
  harvestRecoverDepth?: number;
  harvestRecoverPersistMs?: number;
  /** PEN-01: may she BUY team A (away) / team B (home)? Absent = armed (older games). */
  armA?: boolean;
  armB?: boolean;
  touchPolicy?: 'top' | 'legacy';
  /** What an adding side does while Pinnacle is off (relay default follow = PEN-02). */
  offLinePolicy?: 'follow' | 'pair' | 'puppy';
  /** false = no sell-back rests while Pinnacle is off (Doom 10-14). */
  sellBackWhileOff?: boolean;
  /** With sellBackWhileOff false: true = a resting sell-back stays through the drop, nothing new (Doom 10-18 R3); relay default false. */
  sellBackHoldWhileOff?: boolean;
  followMaxAboveCents?: number;
  followRefMaxAgeMs?: number;
  /** true = a follow buy on the short side takes the short-side entry size (Doom 10-18 R2); relay default false. */
  followShortEntryClip?: boolean;
  /** Follow contracts per remembered Pinnacle reference while off; 0 = no limit (Doom 10-18 F1). */
  followMaxPerRefContracts?: number;
  followEdgeGuard?: boolean;
  touchMinContracts?: number;
  valueConfirmMs?: number;
  /** Split book (Doom R3, 2026-10-16): both top bids posted when each shows value under the ceiling; relay default off / 25 / 1¢ / cancel. */
  splitBookEnabled?: boolean;
  splitClipContracts?: number;
  splitMaxEdgeCents?: number;
  splitOnLiquidityDrop?: 'cancel' | 'keep';
  splitWhileOff?: 'cancel' | 'keep';
  /** Doom 10-16: a new bracket needs the completing side placeable (default on); no completion in overtime (NHL default on). */
  pairNeedsBothSides?: boolean;
  pairNoCompleteInOT?: boolean;
  edgeGraceMs?: number;
  addMaxPerMin?: number;
  restEdgeMaxAgeMs?: number;
  liquidityGatePolicy?: PenelopeLiquidityGatePolicy;
  /** Doom 10-17: the depth dropout's own policy (relay ≥ 2026-10-18); older relays hold. */
  depthDropPolicy?: PenelopeLiquidityGatePolicy;
  variants: PenelopeVariant[];
  /** The mini game's own knobs (Doom 10-17; relay ≥ 10-19 R5). Optional: an older relay sends none. */
  mgOffsetTicks?: number;
  mgLifetimeMs?: number;
  mgStartIntervalMs?: number;
  mgDropPct?: number;
  mgRecoverPct?: number;
  mgConfirmMs?: number;
  mgWindowMs?: number;
  /** NEW-04: 'percent' (default) or 'absolute' (both external best bids under mgAbsThreshold). */
  mgTriggerMethod?: 'percent' | 'absolute';
  mgAbsThreshold?: number;
  /** Doom 10-29: 'pair' (V1, default) or 'value' (one-sided value orders in a side's own dropout). */
  mgMode?: 'pair' | 'value';
  mgValueClipContracts?: number;
  mgValueDropoutCapContracts?: number;
  mgValuePullOnRecover?: boolean;
  mgValuePullOnEdge?: boolean;
  mgValueEdgeFloorCents?: number;
  mgValueExitOnValue?: boolean;
  /** Josh 10-29 review: pair-mode completions must also lock against the orphan's cost (¢). null = off (default). */
  mgCompletionLockCents?: number | null;
  /** Josh 10-29 review: Puppy Love — support checks in a depth-dropout hold (default true), keep a rest when the book shows no
   *  levels (default false), and the lock between two Puppy orders (¢; null = none, the default). */
  puppyHoldSupportInDropout?: boolean;
  puppyNoLevelsKeep?: boolean;
  puppyPairLockCents?: number | null;
  /** Doom 10-29 / 11-01 (+ the 11-02 review's settle): the wall check, the no-ladder window, the probe, the dropout entry. */
  puppyTrialWall?: boolean;
  puppyWallTicks?: number;
  puppyWallLead?: number;
  puppyNoLadderMs?: number;
  puppyDropoutStandard?: boolean;
  puppyAdoptNeedsSupport?: boolean;
  puppyDropoutSettleMs?: number;
  puppyProbeGrowth?: number;
  puppyProbeMs?: number;
  [k: string]: unknown;
}

interface Holding { contracts: number; costCents: number }
/** A side's last decision as the status reads it (PR-063). */
export interface PenelopeWant { kind: 'quote' | 'keep' | 'none'; px: number | null }
export interface PenelopeLive {
  /** false = the baseline is placing real orders on Kalshi (the named variants stay simulated). */
  shadow: boolean;
  gameKey: string | null;
  /** The game-state lane's phase, with its facts (score, clock) when the lane has the game — the anchor strip reads them. */
  phase: {
    phase: 'pre' | 'live' | 'final' | 'unknown';
    reason: string | null;
    state?: { statusId: number; awayTeam: string; homeTeam: string; awayScore: number | null; homeScore: number | null; gameClock: string | null; periodTypeId: number | null } | null;
  };
  baseline: {
    regime: PenelopeRegime;
    gate: string | null;
    stopReason: string | null;
    /** Pinnacle fair per team in cents; A = ticker A's YES team (the away side as attached here). */
    fairC: { A: number | null; B: number | null };
    pinnacleState: 'present' | 'absent' | 'withdrawn' | null;
    fairBlock: string | null;
    vendorAgeMs: number | null;
    /** Relay ≥ 2026-10-15: the line is past max Pinnacle age but kept as still connected. */
    fairStill?: { ageMs: number; driftCents: number | null } | null;
    inv: { A: Holding; B: Holding };
    net: number;
    pnlCents: number;
    /** What rests per side, its venue order and why it was placed (value / unwind / pair: bracket …). */
    rests: { A: PenelopeRest | null; B: PenelopeRest | null };
    /** What each side wants and why, as of the last pass — the reason a side is NOT posting (relay ≥ 10-14). */
    desiredWhy?: { A: string | null; B: string | null };
    /** Follow reference per side: the level Pinnacle last called good value, and how long ago. */
    ref?: { A: { px: number; ageMs: number } | null; B: { px: number; ageMs: number } | null };
    /** Age of Pinnacle's last line while it is off. */
    lastFairAgeMs?: number | null;
    /** The unpaired tail she is selling back (which team, how many, what they cost). */
    tail?: { team: 'A' | 'B'; contracts: number; avgCents: number } | null;
    /** Relay ≥ 2026-10-20 (PR-063): the aux slot (split unit / mini orphan completion), each side's last decision (keep =
     *  retained, `desiredWhy` says why), whether buying a team reduces, and what pacing or the venue held back. */
    aux?: { A: PenelopeRest | null; B: PenelopeRest | null };
    want?: { A: PenelopeWant; B: PenelopeWant };
    auxWant?: { A: PenelopeWant | null; B: PenelopeWant | null };
    auxWhy?: { A: string | null; B: string | null };
    reducing?: { A: boolean; B: boolean };
    heldBack?: string | null;
    venueHold?: { A: string | null; B: string | null };
    regimeAgeMs?: number;
    /** Relay ≥ R10 ("Why?"): per side, the touch / role / unknown hold; per game, the liquidity gate and the scan read. */
    sides?: { A: PenelopeSideNote | null; B: PenelopeSideNote | null };
    liquidityGate?: string | null;
    scanNote?: string | null;
    /** penelope_games.config_version the runner holds (the settings version a screenshot names). */
    configVersion?: number;
    /** R11 (PR-066, log only): what the Island and Flicker detectors see per team — Island = her entry rests on a thin level
     *  above a wall; Flicker = outside depth churning near the bid. Nothing acts on it yet; the Why popover reads it. */
    detect?: Record<'A' | 'B', {
      island: { px: number; ext: number; wallPx: number; wall: number; rescued: boolean; sinceMs: number; would: string } | null;
      flicker: { on: boolean; ratio: number; flips: number } | null;
    } | null> | null;
    lastTrigger?: string | null;
    limitRejects?: number;
    lastError?: string | null;
    places: number;
    cancels: number;
  };
  variants: Array<{ name: string; regime: PenelopeRegime; net: number; pnlCents: number }>;
}

export interface PenelopeGame {
  id: string;
  /** Which strategy the row runs (the relay's column; absent on an older relay = Penelope). */
  strategy?: 'penelope' | 'harvester' | 'minigame';
  eventTicker: string;
  tickerA: string;
  tickerB: string;
  status: string;
  stopReason: string | null;
  config: PenelopeConfig;
  fillsCount: number;
  live: PenelopeLive | null;
  /** Armed but not running, and why. */
  parked: string | null;
}

export interface PenelopeControl {
  game: PenelopeGame | null;
  /** A game can be attached here (supported series, and the caller holds the grant). */
  eligible: boolean;
  reasons: string[];
  /** The relay service is up (PENELOPE=1). */
  serviceRunning: boolean;
  /** May a game of the caller's run LIVE here (box flag AND not locked to practice)? */
  liveEligible: boolean;
  /** Why not, in the trader's words; null when eligible. */
  liveReason: string | null;
  lastStopped: PenelopeLastStopped | null;
  /** Box and per-trader game slots, for the "N of M in use" line; null until read. */
  slots: { running: number; maxArmed: number; mine: number; maxMine: number } | null;
  error: string | null;
  busy: boolean;
  /** Attach disarmed, with `config` as the starting settings (else the relay's defaults). */
  attach: (config?: Partial<PenelopeConfig>) => Promise<void>;
  /** One click: attach this game if it is not attached yet (with `config` as its starting settings), then arm it. */
  run: (config?: Partial<PenelopeConfig>) => Promise<void>;
  patch: (config: Partial<PenelopeConfig>) => Promise<void>;
  setArm: (armed: boolean) => Promise<void>;
  kill: () => Promise<void>;
  remove: () => Promise<void>;
  /** VH-01: the caller's volatility harvester on this event (its own row, its own controls). Absent in old fixtures. */
  harvester?: PenelopeHarvesterControl;
  /** Doom 10-17: the caller's mini game on this event (its own row, relay strategy 'minigame'). */
  minigame?: PenelopeHarvesterControl;
}

/** The volatility harvester's row and controls (relay strategy 'harvester'; practice only unless the box allows live). */
export interface PenelopeHarvesterControl {
  game: PenelopeGame | null;
  liveEligible: boolean;
  /** Attach (strategy 'harvester', with `config` over the relay's harvester start) if needed, then arm. */
  run: (config?: Partial<PenelopeConfig>) => Promise<void>;
  patch: (config: Partial<PenelopeConfig>) => Promise<void>;
  setArm: (armed: boolean) => Promise<void>;
  remove: () => Promise<void>;
  /** This strategy's most recent self-stop on the event within 24 h (relay ≥ 2026-10-21; absent on an older relay). */
  lastStopped?: PenelopeLastStopped | null;
}

/** A game of the caller's that stopped on its own recently (the ticket's "Stopped: <reason>" banner). */
export interface PenelopeLastStopped { stopReason: string | null; at: string; mode: 'live' | 'shadow' }

interface ByEventResponse {
  game: PenelopeGame | null;
  harvester?: PenelopeGame | null;
  minigame?: PenelopeGame | null;
  harvesterLiveEligible?: boolean;
  eligible: boolean;
  reasons: string[];
  /** Box allows live AND the caller is not locked to practice. */
  liveEligible?: boolean;
  advancedEligible?: boolean;
  lastStopped?: PenelopeLastStopped | null;
  /** Relay ≥ 2026-10-21: the sibling strategies' own last self-stop on the event, for their panels. */
  minigameLastStopped?: PenelopeLastStopped | null;
  harvesterLastStopped?: PenelopeLastStopped | null;
  /** The caller's armed games on this box (against health.maxArmedPerTrader). */
  armedByCaller?: number;
  health: { running: boolean; enabled?: boolean; liveAllowed?: boolean; runners?: number; maxArmed?: number; maxArmedPerTrader?: number };
}

/** The Kalshi event a game's Penelope row is keyed by (null when the game has no sibling moneyline legs). */
export function penelopeEventTicker(game: GameRow): string | null {
  const legs = larryLegsOf(game);
  return legs ? eventTickerOfMarket(legs.tickerA) : null;
}

/** What attach and one-click Start send for a game: ticker A is the AWAY market and ticker B the HOME market, so
 *  "team A" is always the away side. Null when the game has no sibling moneyline legs. */
export function penelopeAttachInput(game: GameRow): { eventTicker: string; tickerA: string; tickerB: string; labelA: string; labelB: string; gameKey: string } | null {
  const legs = larryLegsOf(game);
  if (!legs) return null;
  return { eventTicker: eventTickerOfMarket(legs.tickerA), tickerA: legs.tickerA, tickerB: legs.tickerB, labelA: game.awayCode, labelB: game.homeCode, gameKey: game.key };
}

/** `enabled` false polls nothing: the caller has no grant, so there is nothing it may read. */
export function usePenelope(game: GameRow | null, enabled: boolean): PenelopeControl {
  const [data, setData] = useState<ByEventResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Ticker A is the AWAY market and ticker B the HOME market, so "team A" is always the away side on this screen.
  const attachInput = useMemo(() => (game ? penelopeAttachInput(game) : null), [game]);
  const eventTicker = attachInput?.eventTicker ?? null;

  const read = useCallback(
    async (signal: AbortSignal) => {
      if (!eventTicker) return;
      try {
        const res = await apiFetch(`/api/penelope/games/by-event/${encodeURIComponent(eventTicker)}`);
        const body = (await res.json().catch(() => ({}))) as ByEventResponse & { error?: string };
        if (signal.aborted) return;
        if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
        setData(body);
        setError(null);
      } catch (e) {
        if (!signal.aborted) setError((e as Error).message);
      }
    },
    [eventTicker],
  );
  const { refresh } = useVisibilityAwarePoll(read, POLL_MS, { enabled: enabled && eventTicker !== null });

  const call = useCallback(
    async (path: string, method: string, body?: unknown) => {
      setBusy(true);
      try {
        const res = await apiFetch(path, {
          method,
          headers: { 'content-type': 'application/json' },
          body: body === undefined ? undefined : JSON.stringify(body),
        });
        if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error || `HTTP ${res.status}`);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        // Busy until the re-read lands: a switch clicked in between would write the draft (the row still reads as
        // unattached) and the next poll would throw the click away.
        try { await refresh(); } finally { setBusy(false); }
      }
    },
    [refresh],
  );

  const id = data?.game?.id ?? null;
  const hid = data?.harvester?.id ?? null;
  const mid = data?.minigame?.id ?? null;
  /** Attach a sibling-strategy row (harvester, mini game) if it is not there yet, then arm it. */
  const siblingRun = useCallback(async (strategy: 'harvester' | 'minigame', existing: string | null, config?: Partial<PenelopeConfig>) => {
    if (!attachInput) return;
    setBusy(true);
    try {
      const post = async (path: string, body?: unknown): Promise<{ game?: { id?: string } }> => {
        const res = await apiFetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
        const json = (await res.json().catch(() => ({}))) as { error?: string; game?: { id?: string } };
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        return json;
      };
      const gameId = existing ?? (await post('/api/penelope/games', { ...attachInput, strategy, ...(config ? { config } : {}) })).game?.id ?? null;
      if (gameId) await post(`/api/penelope/games/${gameId}/arm`);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      await refresh();
    }
  }, [attachInput, refresh]);
  const minigameRun = useCallback((config?: Partial<PenelopeConfig>) => siblingRun('minigame', mid, config), [siblingRun, mid]);
  const minigamePatch = useCallback(async (config: Partial<PenelopeConfig>) => { if (mid) await call(`/api/penelope/games/${mid}`, 'PATCH', { config }); }, [mid, call]);
  const minigameSetArm = useCallback(async (armed: boolean) => { if (mid) await call(`/api/penelope/games/${mid}/${armed ? 'arm' : 'disarm'}`, 'POST'); }, [mid, call]);
  const minigameRemove = useCallback(async () => { if (mid) await call(`/api/penelope/games/${mid}`, 'DELETE'); }, [mid, call]);
  const minigame = useMemo<PenelopeHarvesterControl>(() => ({
    game: data?.minigame ?? null, liveEligible: data?.liveEligible === true, lastStopped: data?.minigameLastStopped ?? null,
    run: minigameRun, patch: minigamePatch, setArm: minigameSetArm, remove: minigameRemove,
  }), [data?.minigame, data?.liveEligible, data?.minigameLastStopped, minigameRun, minigamePatch, minigameSetArm, minigameRemove]);
  const harvesterRun = useCallback(async (config?: Partial<PenelopeConfig>) => {
    if (!attachInput) return;
    setBusy(true);
    try {
      const post = async (path: string, body?: unknown): Promise<{ game?: { id?: string } }> => {
        const res = await apiFetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
        const json = (await res.json().catch(() => ({}))) as { error?: string; game?: { id?: string } };
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        return json;
      };
      const gameId = hid ?? (await post('/api/penelope/games', { ...attachInput, strategy: 'harvester', ...(config ? { config } : {}) })).game?.id ?? null;
      if (gameId) await post(`/api/penelope/games/${gameId}/arm`);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      await refresh();
    }
  }, [attachInput, hid, refresh]);
  const harvesterPatch = useCallback(async (config: Partial<PenelopeConfig>) => { if (hid) await call(`/api/penelope/games/${hid}`, 'PATCH', { config }); }, [hid, call]);
  const harvesterSetArm = useCallback(async (armed: boolean) => { if (hid) await call(`/api/penelope/games/${hid}/${armed ? 'arm' : 'disarm'}`, 'POST'); }, [hid, call]);
  const harvesterRemove = useCallback(async () => { if (hid) await call(`/api/penelope/games/${hid}`, 'DELETE'); }, [hid, call]);
  const harvester = useMemo<PenelopeHarvesterControl>(() => ({
    game: data?.harvester ?? null, liveEligible: data?.harvesterLiveEligible === true, lastStopped: data?.harvesterLastStopped ?? null,
    run: harvesterRun, patch: harvesterPatch, setArm: harvesterSetArm, remove: harvesterRemove,
  }), [data?.harvester, data?.harvesterLiveEligible, data?.harvesterLastStopped, harvesterRun, harvesterPatch, harvesterSetArm, harvesterRemove]);
  return {
    harvester,
    minigame,
    game: data?.game ?? null,
    eligible: data?.eligible ?? false,
    reasons: data?.reasons ?? [],
    serviceRunning: data?.health.running ?? false,
    liveEligible: data?.liveEligible === true,
    liveReason: !data || data.liveEligible === true ? null
      : data.health.liveAllowed === false ? 'live orders are off on this box'
        : 'a manager set you to practice (shadow) only',
    lastStopped: data?.lastStopped ?? null,
    slots: data && typeof data.health.maxArmed === 'number' && typeof data.health.runners === 'number'
      ? { running: data.health.runners, maxArmed: data.health.maxArmed, mine: data.armedByCaller ?? 0, maxMine: data.health.maxArmedPerTrader ?? Number.POSITIVE_INFINITY }
      : null,
    error,
    busy,
    attach: useCallback(async (config) => { if (attachInput) await call('/api/penelope/games', 'POST', config ? { ...attachInput, config } : attachInput); }, [attachInput, call]),
    run: useCallback(async (config) => {
      if (!attachInput) return;
      setBusy(true);
      try {
        const post = async (path: string, body?: unknown): Promise<{ game?: { id?: string } }> => {
          const res = await apiFetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
          const json = (await res.json().catch(() => ({}))) as { error?: string; game?: { id?: string } };
          if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
          return json;
        };
        // Attach is born disarmed on the relay; arming is its own call, so a failed arm leaves an attached, idle game.
        const gameId = id ?? (await post('/api/penelope/games', config ? { ...attachInput, config } : attachInput)).game?.id ?? null;
        if (gameId) await post(`/api/penelope/games/${gameId}/arm`);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        try { await refresh(); } finally { setBusy(false); }
      }
    }, [attachInput, id, refresh]),
    patch: useCallback(async (config) => { if (id) await call(`/api/penelope/games/${id}`, 'PATCH', { config }); }, [id, call]),
    setArm: useCallback(async (armed) => { if (id) await call(`/api/penelope/games/${id}/${armed ? 'arm' : 'disarm'}`, 'POST'); }, [id, call]),
    kill: useCallback(async () => { if (id) await call(`/api/penelope/games/${id}/kill`, 'POST', { confirm: true }); }, [id, call]),
    remove: useCallback(async () => { if (id) await call(`/api/penelope/games/${id}`, 'DELETE'); }, [id, call]),
  };
}
